import type { FacetMappings } from '#types/filters'
import { mapColorFilters } from '#commerce/utils/filters/mapColorFilters'
import { getFormattedLocale, getSiteId } from '#core/utils/i18n'

const EMPTY_FACET_MAPPINGS: FacetMappings = { colorFacetId: '', colorMapping: {} }
// Keyed by app instance so server requests never share an in-flight load.
const inFlight = new WeakMap<object, Promise<FacetMappings>>()

/**
 * Loads BloomReach colour facet mappings and maps agent colour intents for search.
 * Shared by chat host bindings and the agent-results page so URL hydrates keep `Red` → `Red;hex`.
 */
export function useAgentforceFacetMappings() {
  const { api } = useAppConfig()
  const locale = useLocale()
  const countryCode = useCountryCode()
  const nuxtApp = useNuxtApp()
  const cache = useState<FacetMappings | null>('agentforce-facet-mappings', () => null)

  async function loadFacetMappings(): Promise<FacetMappings> {
    if (cache.value) return cache.value
    const pending = inFlight.get(nuxtApp)
    if (pending) return pending

    // Only successes are cached, so one transient failure does not disable colour mapping for the session.
    const request = $fetch<FacetMappings>('/fapi/agentforce/refinement-map', {
      params: {
        siteId: getSiteId(api.brandPrefix, countryCode).toLowerCase(),
        locale: getFormattedLocale(locale),
      },
    })
      .then((mappings) => {
        cache.value = mappings
        return cache.value
      })
      .catch(() => EMPTY_FACET_MAPPINGS)
      .finally(() => inFlight.delete(nuxtApp))
    inFlight.set(nuxtApp, request)
    return request
  }

  async function mapFilters(filters?: Record<string, any>): Promise<Record<string, any> | undefined> {
    if (!filters) return undefined
    const mappings = await loadFacetMappings()
    return mapColorFilters(filters, mappings)
  }

  return { mapFilters, loadFacetMappings }
}
